/// <amd-module name="VSSUI/Components/PeoplePicker/Suggestions" />
import * as React from "react";
import { css, getSafeId, KeyCode } from "VSSUI/Util";
import { Button } from "VSSUI/Button";
import { Spinner } from "VSSUI/Spinner";
import { ISuggestionsProps } from "OfficeFabric/Pickers";
import { ISuggestionItemProps } from "./Suggestions.Props";

/** @deprecated */
export const enum SuggestionActionType {
    none,
    forceResolve,
    searchMore
}

/** @deprecated */
export interface ISuggestionsState {
    selectedActionType: SuggestionActionType;
}

/** @deprecated */
export class SuggestionsItem<T> extends React.Component<ISuggestionItemProps<T>> {
    public render(): JSX.Element {
        const { suggestionModel, renderSuggestion, onClick, className, onRemoveItem, isSelectedOverride, removeButtonAriaLabel } = this.props;
        return (
            <div
                className={css(
                    "vss-suggestionsItem",
                    "flex-row flex-grow",
                    (suggestionModel.selected || isSelectedOverride) && "is-suggested vss-suggestionsItemIsSuggested",
                    className
                )}
            >
                <div onClick={onClick} className="ms-Suggestions-itemButton itemButton flex-row flex-grow">
                    {renderSuggestion(suggestionModel.item, this.props)}
                </div>
                {this.props.showRemoveButton ? (
                    <Button
                        subtle={true}
                        iconProps={{ iconName: "Cancel", style: { fontSize: "12px" } }}
                        ariaLabel={removeButtonAriaLabel}
                        onClick={onRemoveItem}
                        className={css("ms-Suggestions-closeButton", "closeButton")}
                    />
                ) : null}
            </div>
        );
    }
}

/** @deprecated */
export class Suggestions<T> extends React.Component<ISuggestionsProps<T>, ISuggestionsState> {
    protected _forceResolveButton = React.createRef<Button>();
    protected _searchForMoreButton = React.createRef<Button>();
    protected _selectedElement = React.createRef<HTMLDivElement>();
    private SuggestionsItemOfProperType = SuggestionsItem as new (props: ISuggestionItemProps<T>) => SuggestionsItem<T>;
    private activeSelectedElement: HTMLDivElement | null;
    constructor(suggestionsProps: ISuggestionsProps<T>) {
        super(suggestionsProps);
        this.state = {
            selectedActionType: SuggestionActionType.none
        };
    }

    public componentDidMount(): void {
        this.scrollSelected();
        this.activeSelectedElement = this._selectedElement ? this._selectedElement.current : null;
    }

    public componentDidUpdate(): void {
        // Only scroll to selected element if the selected element has changed. Otherwise do nothing.
        // This prevents some odd behavior where scrolling the active element out of view and clicking on a selected element
        // will trigger a focus event and not give the clicked element the click.
        if (this._selectedElement.current && this.activeSelectedElement !== this._selectedElement.current) {
            this.scrollSelected();
            this.activeSelectedElement = this._selectedElement.current;
        }
    }

    public render(): JSX.Element {
        const {
            forceResolveText,
            mostRecentlyUsedHeaderText,
            searchForMoreText,
            className,
            moreSuggestionsAvailable,
            noResultsFoundText,
            suggestions,
            isLoading,
            isSearching,
            loadingText,
            onRenderNoResultFound,
            searchingText,
            isMostRecentlyUsedVisible,
            resultsMaximumNumber,
            resultsFooterFull,
            resultsFooter,
            isResultsFooterVisible = true,
            suggestionsAvailableAlertText,
            suggestionsHeaderText
        } = this.props;

        const noResults = () => {
            return noResultsFoundText ? (
                <div role="alert" className="ms-Suggestions-none vss-suggestionsNone">
                    {noResultsFoundText}
                </div>
            ) : null;
        };
        // MostRecently Used text should supercede the header text if it's there and available.
        let headerText: string | undefined = suggestionsHeaderText;
        if (isMostRecentlyUsedVisible && mostRecentlyUsedHeaderText) {
            headerText = mostRecentlyUsedHeaderText;
        }
        let footerTitle: ((props: ISuggestionsProps<T>) => JSX.Element) | undefined = undefined;
        if (isResultsFooterVisible) {
            footerTitle = suggestions.length >= (resultsMaximumNumber as number) ? resultsFooterFull : resultsFooter;
        }
        const hasNoSuggestions = (!suggestions || !suggestions.length) && !isLoading;
        return (
            <div className={css("ms-Suggestions", className ? className : "", "vss-suggestions")}>
                {headerText ? <div className={css("ms-Suggestions-title", "vss-suggestionsTitle")}>{headerText}</div> : null}
                {forceResolveText && this._shouldShowForceResolve() && (
                    <Button
                        ref={this._forceResolveButton}
                        className={css(
                            "ms-forceResolve-button actionButton",
                            this.state.selectedActionType === SuggestionActionType.forceResolve && "is-selected buttonSelected"
                        )}
                        onClick={this._forceResolve}
                    >
                        {forceResolveText}
                    </Button>
                )}
                {isLoading && <Spinner className="ms-Suggestions-spinner vss-suggestionsSpinner" label={loadingText} />}
                {hasNoSuggestions ? (onRenderNoResultFound ? onRenderNoResultFound(undefined, noResults) : noResults()) : this._renderSuggestions()}
                {searchForMoreText && moreSuggestionsAvailable && (
                    <Button
                        ref={this._searchForMoreButton}
                        className={css(
                            "ms-SearchMore-button",
                            "actionButton",
                            this.state.selectedActionType === SuggestionActionType.searchMore && "is-selected buttonSelected"
                        )}
                        iconProps={{ iconName: "Search" }}
                        onClick={this._getMoreResults}
                    >
                        {searchForMoreText}
                    </Button>
                )}
                {isSearching ? <Spinner className="ms-Suggestions-spinner vss-suggestionsSpinner" label={searchingText} /> : null}
                {footerTitle && !moreSuggestionsAvailable && !isMostRecentlyUsedVisible && !isSearching ? (
                    <div className="ms-Suggestions-title vss-suggestionsTitle">{footerTitle(this.props)}</div>
                ) : null}
                {
                    <span role="alert" aria-live="polite" className="ms-Suggestions-suggestionsAvailable vss-suggestionsAvailable">
                        {!isLoading && !isSearching && suggestions && suggestions.length > 0 && suggestionsAvailableAlertText
                            ? suggestionsAvailableAlertText
                            : null}
                    </span>
                }
            </div>
        );
    }

    /**
     * Returns true if the event was handled, false otherwise
     */
    public tryHandleKeyDown = (keyCode: number, currentSuggestionIndex: number): boolean => {
        let isEventHandled = false;
        let newSelectedActionType = null;
        const currentSelectedAction = this.state.selectedActionType;
        const suggestionLength = this.props.suggestions.length;
        if (keyCode === KeyCode.downArrow) {
            switch (currentSelectedAction) {
                case SuggestionActionType.forceResolve:
                    if (suggestionLength > 0) {
                        this._refocusOnSuggestions(keyCode);
                        newSelectedActionType = SuggestionActionType.none;
                    } else if (this._searchForMoreButton.current) {
                        newSelectedActionType = SuggestionActionType.searchMore;
                    } else {
                        newSelectedActionType = SuggestionActionType.forceResolve;
                    }
                    break;
                case SuggestionActionType.searchMore:
                    if (this._forceResolveButton.current) {
                        newSelectedActionType = SuggestionActionType.forceResolve;
                    } else if (suggestionLength > 0) {
                        this._refocusOnSuggestions(keyCode);
                        newSelectedActionType = SuggestionActionType.none;
                    } else {
                        newSelectedActionType = SuggestionActionType.searchMore;
                    }
                    break;
                case SuggestionActionType.none:
                    if (currentSuggestionIndex === -1 && this._forceResolveButton.current) {
                        newSelectedActionType = SuggestionActionType.forceResolve;
                    }
                    break;
            }
        } else if (keyCode === KeyCode.upArrow) {
            switch (currentSelectedAction) {
                case SuggestionActionType.forceResolve:
                    if (this._searchForMoreButton.current) {
                        newSelectedActionType = SuggestionActionType.searchMore;
                    } else if (suggestionLength > 0) {
                        this._refocusOnSuggestions(keyCode);
                        newSelectedActionType = SuggestionActionType.none;
                    }
                    break;
                case SuggestionActionType.searchMore:
                    if (suggestionLength > 0) {
                        this._refocusOnSuggestions(keyCode);
                        newSelectedActionType = SuggestionActionType.none;
                    } else if (this._forceResolveButton.current) {
                        newSelectedActionType = SuggestionActionType.forceResolve;
                    }
                    break;
                case SuggestionActionType.none:
                    if (currentSuggestionIndex === -1 && this._searchForMoreButton.current) {
                        newSelectedActionType = SuggestionActionType.searchMore;
                    }
                    break;
            }
        }

        if (newSelectedActionType !== null) {
            this.setState({ selectedActionType: newSelectedActionType });
            isEventHandled = true;
        }

        return isEventHandled;
    };

    public hasSuggestedAction(): boolean {
        return this._searchForMoreButton.current !== undefined || this._forceResolveButton.current !== undefined;
    }

    public hasSuggestedActionSelected(): boolean {
        return this.state.selectedActionType !== SuggestionActionType.none;
    }

    public executeSelectedAction(): void {
        switch (this.state.selectedActionType) {
            case SuggestionActionType.forceResolve:
                this._forceResolve();
                break;
            case SuggestionActionType.searchMore:
                this._getMoreResults();
                break;
        }
    }

    public focusAboveSuggestions(): void {
        if (this._forceResolveButton.current) {
            this.setState({ selectedActionType: SuggestionActionType.forceResolve });
        } else if (this._searchForMoreButton.current) {
            this.setState({ selectedActionType: SuggestionActionType.searchMore });
        }
    }

    public focusBelowSuggestions(): void {
        if (this._searchForMoreButton.current) {
            this.setState({ selectedActionType: SuggestionActionType.searchMore });
        } else if (this._forceResolveButton.current) {
            this.setState({ selectedActionType: SuggestionActionType.forceResolve });
        }
    }

    public focusSearchForMoreButton(): void {
        if (this._searchForMoreButton.current) {
            this._searchForMoreButton.current.focus();
        }
    }

    // TODO get the element to scroll into view properly regardless of direction.
    public scrollSelected(): void {
        if (this._selectedElement.current && this._selectedElement.current.scrollIntoView !== undefined) {
            this._selectedElement.current.scrollIntoView(false);
        }
    }

    private _renderSuggestions(): JSX.Element {
        const {
            onRenderSuggestion,
            removeSuggestionAriaLabel,
            suggestionsItemClassName,
            resultsMaximumNumber,
            showRemoveButtons,
            suggestionsContainerAriaLabel
        } = this.props;
        let { suggestions } = this.props;
        const TypedSuggestionsItem = this.SuggestionsItemOfProperType;

        if (resultsMaximumNumber) {
            suggestions = suggestions.slice(0, resultsMaximumNumber);
        }

        return (
            <div
                aria-label={suggestionsContainerAriaLabel}
                className="ms-Suggestions-container vss-suggestionsContainer"
                id={getSafeId("suggestion-list")}
                role="list"
            >
                {suggestions.map((suggestion, index) => (
                    <div
                        aria-label={suggestion.ariaLabel}
                        // tslint:disable-next-line:no-string-literal
                        key={(suggestion.item as any)["key"] ? (suggestion.item as any)["key"] : index}
                        id={getSafeId("sug-" + index)}
                        ref={suggestion.selected ? this._selectedElement : ""}
                        role="listitem"
                    >
                        <TypedSuggestionsItem
                            className={suggestionsItemClassName}
                            id={getSafeId("sug-item" + index)}
                            onClick={this._onClickTypedSuggestionsItem(suggestion.item, index)}
                            onRemoveItem={this._onRemoveTypedSuggestionsItem(suggestion.item, index)}
                            removeButtonAriaLabel={removeSuggestionAriaLabel}
                            renderSuggestion={onRenderSuggestion as any}
                            showRemoveButton={showRemoveButtons}
                            suggestionModel={suggestion}
                        />
                    </div>
                ))}
            </div>
        );
    }

    private _getMoreResults = (): void => {
        if (this.props.onGetMoreResults) {
            this.props.onGetMoreResults();
        }
    };

    private _forceResolve = (): void => {
        if (this.props.createGenericItem) {
            this.props.createGenericItem();
        }
    };

    private _shouldShowForceResolve = (): boolean => {
        return this.props.showForceResolve ? this.props.showForceResolve() : false;
    };

    private _onClickTypedSuggestionsItem = (item: T, index: number): ((ev?: React.MouseEvent<HTMLElement>) => void) => {
        return (ev?: React.MouseEvent<HTMLElement>): void => {
            this.props.onSuggestionClick(ev, item, index);
        };
    };

    private _refocusOnSuggestions = (keyCode: number): void => {
        if (typeof this.props.refocusSuggestions === "function") {
            this.props.refocusSuggestions(keyCode);
        }
    };

    private _onRemoveTypedSuggestionsItem = (item: T, index: number): ((ev: React.MouseEvent<HTMLElement>) => void) => {
        return (ev: React.MouseEvent<HTMLElement>): void => {
            const onSuggestionRemove = this.props.onSuggestionRemove!;
            onSuggestionRemove(ev, item, index);
            ev.stopPropagation();
        };
    };
}
